import { apiGet, apiWrite, type Problem } from "@/api/client";
import type { ConfirmRefusal } from "@/components/ConfirmDialog";
import {
  doneState,
  errorState,
  type FormState,
  parseFormData,
} from "@/lib/formState";
import type { Violation } from "@shared/publishGate";
import {
  rungFormSchema,
  TEMPLATE_MULTI_FIELDS,
  templateEditFormSchema,
  templateFormSchema,
  templateWordingFormSchema,
} from "@shared/validation";

// Nothing the form renders can name an archived skill or trait — both pickers
// only offer live ones — so these two report a list that went stale between
// the page rendering and the save, and say to reload rather than to edit.
const BINDING_FIELD_ERRORS: Record<string, Record<string, string[]>> = {
  "duplicate-code": {
    code: ["A quest template with this code already exists."],
  },
  skillIds: {
    skillIds: [
      "A skill picked here has been archived since this page loaded. Reload and pick again.",
    ],
  },
  requiresTraitIds: {
    requiresTraitIds: [
      "A trait picked here has been archived since this page loaded. Reload and pick again.",
    ],
  },
};

// The API answers a stale binding as a field error naming the field; the
// wording the form shows is its own, because "reload and pick again" is advice
// only a form can act on.
const bindingErrors = (problem: Problem): Record<string, string[]> => {
  if (problem.type.endsWith("/duplicate-code")) {
    return BINDING_FIELD_ERRORS["duplicate-code"];
  }

  const field = Object.keys(problem.fieldErrors ?? {})[0];

  return (
    BINDING_FIELD_ERRORS[field] ?? {
      code: [problem.detail],
    }
  );
};

const slugOf = (problem: Problem): string =>
  problem.type.replace("/problems/", "");

export async function createTemplateAction(
  _prevState: FormState,
  formData: FormData,
): Promise<FormState> {
  const parsed = parseFormData(
    templateFormSchema,
    formData,
    TEMPLATE_MULTI_FIELDS,
  );

  if (!parsed.wasParsed) {
    return parsed.state;
  }

  const { assignment, ...fields } = parsed.value;

  const result = await apiWrite<{ id: number; createdTags: string[] }>(
    "/templates",
    { method: "POST", body: { ...fields, assignmentTemplate: assignment } },
  );

  if (!result.isOk) {
    return {
      ...errorState(parsed.values, bindingErrors(result.problem)),
      listValues: parsed.listValues,
    };
  }

  // The ladder editor only appears once there is a row for a rung to point at,
  // so creating lands on the detail page rather than back at the listing.
  return doneState(`/quests/templates/${result.value.id}`);
}

const STRUCTURAL_LOCK_FIELDS: Record<string, Record<string, string[]>> = {
  "Goal type is locked": {
    goalType: [
      "This template already has rungs, and changing the goal type would break every in-flight attempt. Archive the rungs first.",
    ],
  },
  "Metric is locked": {
    metricId: [
      "This template already has rungs, and changing the metric would change what those attempts are measuring.",
    ],
  },
};

export async function updateTemplateAction(
  id: number,
  _prevState: FormState,
  formData: FormData,
): Promise<FormState> {
  const parsed = parseFormData(
    templateEditFormSchema,
    formData,
    TEMPLATE_MULTI_FIELDS,
  );

  if (!parsed.wasParsed) {
    return parsed.state;
  }

  const failed = (fieldErrors: Record<string, string[]>): FormState => ({
    ...errorState(parsed.values, fieldErrors),
    listValues: parsed.listValues,
  });

  const result = await apiWrite<{ createdTags: string[] }>(`/templates/${id}`, {
    method: "PUT",
    body: parsed.value,
  });

  if (!result.isOk) {
    // Both locks report a request that is well formed but asks for something
    // the existing rungs forbid, so each lands on the field that asked.
    if (slugOf(result.problem) === "structural-lock") {
      return failed(
        STRUCTURAL_LOCK_FIELDS[result.problem.title] ?? {
          goalType: [result.problem.detail],
        },
      );
    }

    return failed(bindingErrors(result.problem));
  }

  return {
    status: "idle",
    fieldErrors: {},
    values: parsed.values,
    listValues:
      result.value.createdTags.length > 0
        ? { createdTags: result.value.createdTags }
        : undefined,
  };
}

// The default locale is a deployment fact -- it only changes by migration --
// so it is fetched once per session rather than before every save.
let defaultLocaleCode: string | undefined;

const findDefaultLocaleCode = async (): Promise<string> => {
  defaultLocaleCode ??= (
    await apiGet<{ code: string; isDefault: boolean }[]>("/locales")
  ).find((locale) => locale.isDefault)?.code;

  if (!defaultLocaleCode) {
    throw new Error("No default locale is configured.");
  }

  return defaultLocaleCode;
};

export async function updateWordingAction(
  id: number,
  _prevState: FormState,
  formData: FormData,
): Promise<FormState> {
  const parsed = parseFormData(templateWordingFormSchema, formData);

  if (!parsed.wasParsed) {
    return parsed.state;
  }

  const localeCode = await findDefaultLocaleCode();

  const result = await apiWrite<{ staledLocales: string[] }>(
    `/templates/${id}/translations/${localeCode}`,
    {
      method: "PUT",
      body: {
        title: parsed.value.title,
        assignmentTemplate: parsed.value.assignment,
      },
    },
  );

  if (!result.isOk) {
    return errorState(parsed.values, {
      title: ["This template no longer exists."],
    });
  }

  // Writing the source locale stales every translation of it, and that is the
  // one consequence of saving wording that is invisible on this page.
  return {
    status: "idle",
    fieldErrors: {},
    values: {},
    listValues:
      result.value.staledLocales.length > 0
        ? { staledLocales: result.value.staledLocales }
        : undefined,
  };
}

const LADDER_MESSAGES = {
  difficulty: {
    field: "difficultyLevel",
    message:
      "Another active rung already carries that difficulty. Archive it first, or pick a different level.",
  },
  target: {
    field: "targetValue",
    message:
      "Another active rung already carries that target. Two rungs at the same number are the same rung.",
  },
  "manual-single-rung": {
    field: "difficultyLevel",
    message:
      "A MANUAL template has exactly one rung, and this one already has it.",
  },
} as const;

type LadderConflict = keyof typeof LADDER_MESSAGES;

// The goal-shape CHECKs read goal_type, which the rung form never posts, so
// they arrive as field errors the API already worded. A ladder clash arrives
// as a 409 naming which of the two rungs collided.
const rungErrors = (
  problem: Problem,
  values: Record<string, string>,
  missingMessage: string,
): FormState => {
  if (problem.fieldErrors) {
    return errorState(values, problem.fieldErrors);
  }

  if (slugOf(problem) === "ladder-conflict") {
    const conflict = (problem as { conflict?: LadderConflict }).conflict;
    const { field, message } =
      LADDER_MESSAGES[conflict ?? "difficulty"] ?? LADDER_MESSAGES.difficulty;

    return errorState(values, { [field]: [message] });
  }

  return errorState(values, { difficultyLevel: [missingMessage] });
};

export async function createRungAction(
  templateId: number,
  _prevState: FormState,
  formData: FormData,
): Promise<FormState> {
  const parsed = parseFormData(rungFormSchema, formData);

  if (!parsed.wasParsed) {
    return parsed.state;
  }

  const result = await apiWrite<{ id: number }>(
    `/templates/${templateId}/quests`,
    { method: "POST", body: parsed.value },
  );

  if (!result.isOk) {
    return rungErrors(
      result.problem,
      parsed.values,
      "This template no longer exists.",
    );
  }

  return { status: "idle", fieldErrors: {}, values: {} };
}

export async function updateRungAction(
  _templateId: number,
  rungId: number,
  _prevState: FormState,
  formData: FormData,
): Promise<FormState> {
  const parsed = parseFormData(rungFormSchema, formData);

  if (!parsed.wasParsed) {
    return parsed.state;
  }

  const result = await apiWrite(`/quests/${rungId}`, {
    method: "PUT",
    body: parsed.value,
  });

  if (!result.isOk) {
    return rungErrors(
      result.problem,
      parsed.values,
      "This rung no longer exists.",
    );
  }

  // Echoes back what was written rather than clearing, so the field is not
  // repainted with the value that was just replaced.
  return { status: "idle", fieldErrors: {}, values: parsed.values };
}

export async function archiveRungAction(
  _templateId: number,
  rungId: number,
): Promise<ConfirmRefusal> {
  await apiWrite(`/quests/${rungId}/deactivate`, { method: "POST" });

  return null;
}

// Restoring can conflict where archiving never does: the ladder uniques only
// see active rungs, so a difficulty or target reused since is a real clash.
export async function restoreRungAction(
  _templateId: number,
  rungId: number,
): Promise<ConfirmRefusal> {
  const result = await apiWrite(`/quests/${rungId}/activate`, {
    method: "POST",
  });

  if (!result.isOk && slugOf(result.problem) === "ladder-conflict") {
    return { refusal: result.problem.detail };
  }

  return null;
}

export async function archiveTemplateAction(
  id: number,
): Promise<ConfirmRefusal> {
  const result = await apiWrite(`/templates/${id}/archive`, { method: "POST" });

  if (result.isOk) {
    return null;
  }

  return {
    refusal:
      slugOf(result.problem) === "not-found"
        ? "This template no longer exists."
        : result.problem.detail,
  };
}

// The listing only offers this on never-published rows, but the API checks
// again: a publish between the page rendering and the press refuses in place
// rather than discarding content that just went live.
export async function discardTemplateAction(
  id: number,
): Promise<ConfirmRefusal> {
  const result = await apiWrite(`/templates/${id}`, { method: "DELETE" });

  if (result.isOk) {
    return null;
  }

  return {
    refusal:
      slugOf(result.problem) === "not-found"
        ? "This template no longer exists."
        : "This template has been published since the page loaded, so its history is permanent and it can only be archived.",
  };
}

// Restoring lands on DRAFT rather than PUBLISHED. Going straight back to
// published would put wording and rungs in front of users without re-running
// the gate they were archived out of.
export async function restoreTemplateAction(id: number): Promise<void> {
  await apiWrite(`/templates/${id}/restore`, { method: "POST" });
}

export type PublishState =
  | { outcome: "idle" }
  | { outcome: "published"; revisionNo: number }
  | { outcome: "rejected"; violations: Violation[] }
  | { outcome: "error"; message: string };

export async function publishTemplateAction(
  id: number,
  _prevState: PublishState,
): Promise<PublishState> {
  const result = await apiWrite<{ revisionNo: number }>(
    `/templates/${id}/publish`,
    { method: "POST" },
  );

  if (result.isOk) {
    return { outcome: "published", revisionNo: result.value.revisionNo };
  }

  if (slugOf(result.problem) === "publish-gate") {
    return {
      outcome: "rejected",
      violations: (result.problem.violations ?? []) as Violation[],
    };
  }

  return {
    outcome: "error",
    message:
      slugOf(result.problem) === "concurrent-publish"
        ? "Another publish claimed this revision number first. Reload and try again."
        : "This template no longer exists.",
  };
}
